import { writable, derived, get } from 'svelte/store';
import { z } from 'zod';
import { browser } from '$app/environment';
import { isLoading } from '$lib/stores/loading';
import type { User } from '$lib/types/user';
import { UserSchema } from '$lib/types/user';

// Response schema for refresh token API
const RefreshTokenResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  token: z.string().optional().nullable(),
  userData: UserSchema.optional()
});

interface AuthState {
  accessToken: string | null;
  user: User;
  isAuthenticated: boolean;
  isInitialized: boolean;
}

const AUTH_TOKEN_KEY = 'auth_token';
const USER_DATA_KEY = 'user_data';
const AUTH_STORAGE_MODE_KEY = 'auth_storage_mode';
const FORCED_LOGOUT_KEY = 'auth_forced_logout';
const NULL_GUID = '00000000-0000-0000-0000-000000000000';
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type StorageMode = 'local' | 'session';

// Validator function for user data
const validateUserData = (data: unknown): User => {
  try {
    return UserSchema.parse(data);
  } catch (error) {
    console.error('User data validation error:', error);
    return {
      // Null GUID
      id: '00000000-0000-0000-0000-000000000000',
      email: null,
      companyName: null,
      role: null,
      companyId: null,
      websiteUrl: null,
      isImpersonating: false,
      originalRole: null
    };
  }
};

const defaultUser: User = {
  id: NULL_GUID,
  email: null,
  companyName: null,
  role: null,
  companyId: null,
  websiteUrl: null,
  isImpersonating: false,
  originalRole: null
};

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function asGuidOrNull(value: unknown): string | null {
  const text = asNonEmptyString(value);
  if (!text) return null;
  return UUID_REGEX.test(text) ? text : null;
}

function asBooleanOrNull(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    if (lower === 'true') return true;
    if (lower === 'false') return false;
  }
  return null;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;

    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function getFirstClaim(payload: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      return payload[key];
    }
  }
  return undefined;
}

function getRoleClaims(payload: Record<string, unknown>): string[] {
  const roleValue = getFirstClaim(payload, [
    'role',
    'roles',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
  ]);

  if (Array.isArray(roleValue)) {
    return roleValue
      .map((value) => asNonEmptyString(value))
      .filter((value): value is string => Boolean(value));
  }

  const single = asNonEmptyString(roleValue);
  return single ? [single] : [];
}

function deriveUserFromToken(token: string): Partial<User> {
  const payload = decodeJwtPayload(token);
  if (!payload) return {};

  const tokenRoles = getRoleClaims(payload);
  const primaryRole = tokenRoles[0] ?? null;

  return {
    id: asGuidOrNull(
      getFirstClaim(payload, [
        'nameid',
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
      ])
    ) ?? NULL_GUID,
    email: asNonEmptyString(
      getFirstClaim(payload, [
        'email',
        'unique_name',
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
      ])
    ),
    companyName: asNonEmptyString(getFirstClaim(payload, ['companyName'])),
    role: primaryRole,
    roles: tokenRoles.length > 0 ? tokenRoles : null,
    companyId: asGuidOrNull(getFirstClaim(payload, ['companyId', 'CompanyId'])),
    websiteUrl: asNonEmptyString(getFirstClaim(payload, ['websiteUrl'])),
    productSearchUrl: asNonEmptyString(getFirstClaim(payload, ['productSearchUrl'])),
    isImpersonating: asBooleanOrNull(getFirstClaim(payload, ['isImpersonating', 'IsImpersonating'])),
    originalRole: asNonEmptyString(getFirstClaim(payload, ['originalRole', 'OriginalRole']))
  };
}

function mergeUserDataWithToken(token: string, userData: unknown): User {
  const source = (userData && typeof userData === 'object') ? (userData as Partial<User>) : {};
  const tokenUser = deriveUserFromToken(token);

  const sourceId = asGuidOrNull(source.id);
  const resolvedId = tokenUser.id && tokenUser.id !== NULL_GUID
    ? tokenUser.id
    : sourceId ?? NULL_GUID;

  const sourceRoles = Array.isArray(source.roles)
    ? source.roles
        .map((value) => asNonEmptyString(value))
        .filter((value): value is string => Boolean(value))
    : [];

  const resolvedRole =
    tokenUser.role
    ?? asNonEmptyString(source.role)
    ?? (sourceRoles[0] ?? null);

  const resolvedRoles =
    tokenUser.roles && tokenUser.roles.length > 0
      ? tokenUser.roles
      : sourceRoles.length > 0
        ? sourceRoles
        : resolvedRole
          ? [resolvedRole]
          : null;

  const merged: User = validateUserData({
    id: resolvedId,
    email: tokenUser.email ?? asNonEmptyString(source.email),
    companyName: tokenUser.companyName ?? asNonEmptyString(source.companyName),
    role: resolvedRole,
    roles: resolvedRoles,
    companyId: tokenUser.companyId ?? asGuidOrNull(source.companyId),
    websiteUrl: asNonEmptyString(source.websiteUrl) ?? tokenUser.websiteUrl,
    productSearchUrl: asNonEmptyString(source.productSearchUrl) ?? tokenUser.productSearchUrl,
    // Prefer token-derived impersonation state to avoid stale local user_data during
    // impersonation start/stop transitions.
    isImpersonating: tokenUser.isImpersonating ?? asBooleanOrNull(source.isImpersonating) ?? false,
    originalRole: asNonEmptyString(source.originalRole) ?? tokenUser.originalRole
  });

  return merged;
}

function clearStoredAccessToken(): void {
  if (!browser || typeof localStorage === 'undefined') return;

  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
    }
  } catch (e) {
    console.error('Feil ved fjerning av lagret auth token', e);
  }
}

function resolveStorageMode(remember?: boolean): StorageMode {
  if (remember === true) return 'local';
  if (remember === false) return 'session';

  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(AUTH_STORAGE_MODE_KEY) === 'session') {
      return 'session';
    }
    if (localStorage.getItem(AUTH_STORAGE_MODE_KEY) === 'local') {
      return 'local';
    }
    const hasSessionUserData = typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem(USER_DATA_KEY);
    const hasLocalUserData = !!localStorage.getItem(USER_DATA_KEY);
    if (hasSessionUserData && !hasLocalUserData) return 'session';
    if (hasLocalUserData && !hasSessionUserData) return 'local';
  } catch {
    // Ignore and use default.
  }

  // Default to local persistence to preserve existing behavior when no explicit preference exists.
  return 'local';
}

function setStorageMode(mode: StorageMode): void {
  if (!browser || typeof localStorage === 'undefined') return;

  try {
    if (mode === 'session') {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(AUTH_STORAGE_MODE_KEY, 'session');
      }
      localStorage.removeItem(AUTH_STORAGE_MODE_KEY);
      return;
    }

    localStorage.setItem(AUTH_STORAGE_MODE_KEY, 'local');
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(AUTH_STORAGE_MODE_KEY);
    }
  } catch {
    // best-effort metadata
  }
}

function storeToken(_token: string, remember?: boolean): void {
  if (!browser || typeof localStorage === 'undefined') return;

  try {
    if (remember !== undefined) {
      setStorageMode(resolveStorageMode(remember));
    }
    // Access-tokenet skal bare leve i minne. Refresh-cookie er den persistente browser-sessionen.
    clearStoredAccessToken();
  } catch (e) {
    console.error('Feil ved lagring av auth token', e);
  }
}

function storeUserData(userData: User, remember?: boolean): void {
  if (!browser || typeof localStorage === 'undefined') return;
  try {
    const serialized = JSON.stringify(userData);
    const mode = resolveStorageMode(remember);

    if (mode === 'local') {
      localStorage.setItem(USER_DATA_KEY, serialized);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(USER_DATA_KEY);
      }
      return;
    }

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(USER_DATA_KEY, serialized);
    }
    // Prevent stale persisted user_data from shadowing session-only login.
    localStorage.removeItem(USER_DATA_KEY);
  } catch (e) {
    console.error('Feil ved lagring av user data', e);
  }
}

// get
function getStoredUserData(): User | null {
  if (!browser || typeof localStorage === 'undefined') return null;

  try {
    const sessionMode = typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(AUTH_STORAGE_MODE_KEY)
      : null;
    const localMode = localStorage.getItem(AUTH_STORAGE_MODE_KEY);
    const mode: StorageMode | null =
      sessionMode === 'session' ? 'session' :
      localMode === 'local' ? 'local' :
      null;

    if (mode === 'session' && typeof sessionStorage !== 'undefined') {
      const sessionUserData = sessionStorage.getItem(USER_DATA_KEY);
      return sessionUserData ? (JSON.parse(sessionUserData) as User) : null;
    }

    if (mode === 'local') {
      const userData = localStorage.getItem(USER_DATA_KEY);
      return userData ? (JSON.parse(userData) as User) : null;
    }

    // Legacy fallback when storage mode key doesn't exist yet: prefer session.
    if (typeof sessionStorage !== 'undefined') {
      const sessionUserData = sessionStorage.getItem(USER_DATA_KEY);
      if (sessionUserData) return JSON.parse(sessionUserData) as User;
    }

    const userData = localStorage.getItem(USER_DATA_KEY);
    return userData ? (JSON.parse(userData) as User) : null;
  } catch (e) {
    console.error('Feil ved henting av user data', e);
    return null;
  }
}

function clearStoredUserData(): void {
  if (!browser || typeof localStorage === 'undefined') return;

  try {
    localStorage.removeItem(USER_DATA_KEY);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(USER_DATA_KEY);
    }
  } catch (e) {
    console.error('Feil ved fjerning av user data', e);
  }
}

function clearStoredToken(): void {
  if (!browser || typeof localStorage === 'undefined') return;

  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_STORAGE_MODE_KEY);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      sessionStorage.removeItem(AUTH_STORAGE_MODE_KEY);
    }
  } catch (e) {
    console.error('Feil ved fjerning av auth token', e);
  }
}

function isForcedLoggedOut(): boolean {
  if (!browser || typeof localStorage === 'undefined') return false;
  try {
    if (localStorage.getItem(FORCED_LOGOUT_KEY) === '1') return true;
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem(FORCED_LOGOUT_KEY) === '1';
    }
  } catch (e) {
    console.error('Feil ved lesing av forced logout flag', e);
  }
  return false;
}

function setForcedLoggedOut(): void {
  if (!browser || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(FORCED_LOGOUT_KEY, '1');
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(FORCED_LOGOUT_KEY, '1');
    }
  } catch (e) {
    console.error('Feil ved lagring av forced logout flag', e);
  }
}

function clearForcedLoggedOut(): void {
  if (!browser || typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(FORCED_LOGOUT_KEY);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(FORCED_LOGOUT_KEY);
    }
  } catch (e) {
    console.error('Feil ved fjerning av forced logout flag', e);
  }
}

function isJwtExpired(token: string, skewSeconds = 30): boolean {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return true;

    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json) as { exp?: number };

    if (typeof payload.exp !== 'number') return true;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp <= now + skewSeconds;
  } catch {
    return true;
  }
}

const createAuthStore = () => {
  const initialState: AuthState = {
    accessToken: null,
    user: defaultUser,
    isAuthenticated: false,
    isInitialized: false
  };

  const { subscribe, set, update } = writable<AuthState>(initialState);
  let initPromise: Promise<void> | null = null;
  let refreshPromise: Promise<void> | null = null;
  let authRevision = 0;
  let refreshRevision = 0;
  let lastTransientRefreshFailureAt = 0;
  const TRANSIENT_REFRESH_GRACE_MS = 30_000;

  const clearClientAuthState = (): void => {
    authRevision += 1;
    clearStoredToken();
    clearStoredUserData();
    set({
      accessToken: null,
      user: defaultUser,
      isAuthenticated: false,
      isInitialized: true
    });
  };

  const markInitializedOnly = (): void => {
    update(state => ({
      ...state,
      isInitialized: true
    }));
  };

  const rememberTransientRefreshFailure = (): void => {
    lastTransientRefreshFailureAt = Date.now();
  };

  const clearTransientRefreshFailure = (): void => {
    lastTransientRefreshFailureAt = 0;
  };

  const hasRecentTransientRefreshFailure = (): boolean =>
    lastTransientRefreshFailureAt > 0
    && Date.now() - lastTransientRefreshFailureAt <= TRANSIENT_REFRESH_GRACE_MS;

  const runRefreshAccessToken = async (revision: number): Promise<void> => {
    if (!browser) {
      markInitializedOnly();
      return;
    }
    try {
      isLoading.set(true);

      // Hent nåværende token hvis det finnes.
      const currentToken = get({ subscribe }).accessToken;

      // Konfigurer headers basert på om vi har et token
      const headers: HeadersInit = {};
      if (currentToken) {
        headers['Authorization'] = `Bearer ${currentToken}`;
      }

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const rawData = await response.json();
        if (revision !== authRevision) return;

        try {
          // Validate the response data
          const validatedData = RefreshTokenResponseSchema.parse(rawData);

          if (validatedData.success && validatedData.token) {
            const userData = mergeUserDataWithToken(validatedData.token, validatedData.userData ?? undefined);

            clearForcedLoggedOut();
            clearTransientRefreshFailure();

            // Oppdater state. Access-token lagres bare i minne.
            update(state => ({
              ...state,
              accessToken: validatedData.token ?? null,
              user: userData,
              isAuthenticated: true,
              isInitialized: true
            }));

            storeUserData(userData);
            storeToken(validatedData.token);

            return;
          }
        } catch (error) {
          console.error('Token refresh validation error:', error);
        }
      }

      if (revision !== authRevision) return;

      // Ved eksplisitt auth-feil nullstilles sesjonen.
      if (response.status === 401 || response.status === 403) {
        clearTransientRefreshFailure();
        clearClientAuthState();
        return;
      }

      // Ved midlertidige eller uventede backend-svar beholdes nåværende state.
      // En kald browser forblir uinitialisert slik at route guards ikke tolker
      // en uavklart refresh-cookie som en autoritativ utlogging.
      rememberTransientRefreshFailure();
    } catch (error) {
      if (revision !== authRevision) return;
      console.error('Token refresh error:', error);

      // Nettverksfeil etter dvale er uavklart. Behold både aktiv state og en
      // kald, uinitialisert state til en senere refresh kan avgjøre sesjonen.
      rememberTransientRefreshFailure();
    } finally {
      isLoading.set(false);
    }
  };

  const refreshAccessToken = async (): Promise<void> => {
    if (refreshPromise) {
      const requestedRevision = authRevision;
      const pendingRevision = refreshRevision;
      await refreshPromise;
      // A start/stop/login can supersede a refresh already in flight. Its response
      // is discarded; obtain user data for the new session instead of joining it.
      if (pendingRevision !== requestedRevision && authRevision === requestedRevision)
        return refreshAccessToken();
      return;
    }

    refreshRevision = authRevision;
    const revision = authRevision;
    refreshPromise = (async () => {
      try {
        await runRefreshAccessToken(revision);
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  const initialize = async (): Promise<void> => {
    if (initPromise) return initPromise;
    if (!browser) {
      update(state => ({
        ...state,
        isInitialized: true
      }));
      return;
    }
    initPromise = (async () => {
      try {
        isLoading.set(true);

        const forcedLoggedOut = isForcedLoggedOut();
        clearStoredAccessToken();

        if (forcedLoggedOut) {
          clearStoredToken();
          clearStoredUserData();
          set({
            accessToken: null,
            user: defaultUser,
            isAuthenticated: false,
            isInitialized: true
          });
        } else {
          const currentState = get({ subscribe });

          if (currentState.accessToken && !isJwtExpired(currentState.accessToken)) {
            update(state => ({
              ...state,
              isAuthenticated: true,
              isInitialized: true
            }));
          } else {
            // Browser-sessionen gjenopprettes fra HttpOnly refresh-cookie, ikke lagret JWT.
            await refreshAccessToken();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearStoredToken();
        set({
          accessToken: null,
          user: defaultUser,
          isAuthenticated: false,
          isInitialized: true
        });
      } finally {
        isLoading.set(false);
        // Nullstill initPromise for å tillate re-initialisering ved behov
        initPromise = null;
      }
    })();

    return initPromise;
  };

  const login = async (token: string, userData: any, remember: boolean = true): Promise<void> => {
    try {
      const validatedUser = mergeUserDataWithToken(token, userData);
      authRevision += 1;

      // Oppdater state
      set({
        accessToken: token,
        user: validatedUser,
        isAuthenticated: true,
        isInitialized: true
      });

      clearForcedLoggedOut();

      // Lagre token
      storeToken(token, remember);
      // Lagre brukerdata
      storeUserData(validatedUser, remember);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    authRevision += 1;
    try {
      let currentAccessToken = get({ subscribe }).accessToken;

      // Ensure logout endpoint gets a fresh bearer token so server can revoke device/cookies.
      if (!currentAccessToken || isJwtExpired(currentAccessToken, 0)) {
        await refreshAccessToken();
        currentAccessToken = get({ subscribe }).accessToken;
      }

      // Kall logout-endepunktet
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: currentAccessToken
          ? { Authorization: `Bearer ${currentAccessToken}` }
          : undefined
      });
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Uansett, fjern token og tilbakestill state
      setForcedLoggedOut();
      clearClientAuthState();
    }
  };

  const updateToken = (token: string): void => {
    authRevision += 1;
    const currentUserData = get({ subscribe }).user;
    const hydratedUser = mergeUserDataWithToken(token, currentUserData);

    update(state => ({
      ...state,
      accessToken: token,
      user: hydratedUser,
      isAuthenticated: true,
      isInitialized: true
    }));

    clearForcedLoggedOut();
    storeUserData(hydratedUser);

    // Lagre token
    storeToken(token);
  };

  const getAccessToken = async (): Promise<string | null> => {
    await initialize();
    const state = get({ subscribe });
    if (!state.accessToken || isJwtExpired(state.accessToken, 0)) {
      if (state.accessToken && hasRecentTransientRefreshFailure()) {
        return state.accessToken;
      }
      await refreshAccessToken();
      return get({ subscribe }).accessToken;
    }
    return state.accessToken;
  };

  // Impersonerings-funksjoner
  const startImpersonation = async (companyId: string): Promise<void> => {
    try {
      isLoading.set(true);

      const token = await getAccessToken();
      const response = await fetch(`/api/superadmin/impersonate/${companyId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to start impersonation for ${companyId}: ${response.status} ${errorText}`);
        throw new Error(`Failed to start impersonation: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      if (!data.token) {
        throw new Error('No token received after starting impersonation.');
      }

      console.log(`Successfully started impersonation for ${companyId}, received new token.`);

      // Oppdater token og brukerdata
      updateToken(data.token);

      // Hent brukerdata på nytt for å reflektere impersonerings-tilstand
      await refreshAccessToken();
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    } finally {
      isLoading.set(false);
    }
  };

  const stopImpersonation = async (): Promise<void> => {
    try {
      isLoading.set(true);

      const token = await getAccessToken();
      const response = await fetch('/api/superadmin/stop-impersonating', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to stop impersonation: ${response.status} ${errorText}`);
        throw new Error(`Failed to stop impersonation: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      if (!data.token) {
        throw new Error('No token received after stopping impersonation.');
      }

      console.log('Successfully stopped impersonation, received new token.');

      // Oppdater token og brukerdata
      updateToken(data.token);

      // Hent brukerdata på nytt for å reflektere at impersonering er avsluttet
      await refreshAccessToken();
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      throw error;
    } finally {
      isLoading.set(false);
    }
  };
  if (browser) {
    initialize();
  }
  return {
    subscribe,
    login,
    logout,
    updateToken,
    refreshAccessToken,
    initialize,
    getAccessToken,
    clearToken: clearStoredToken,
    startImpersonation,
    stopImpersonation
  };
};

export const auth = createAuthStore();
export const isAuthenticated = derived(auth, $auth => $auth.isAuthenticated);
export const isInitialized = derived(auth, $auth => $auth.isInitialized);
export const currentUser = derived(auth, $auth => $auth.user);

/**
 * A derived store that returns the current companyId if impersonating, otherwise null.
 */
export const activeFirmaId = derived(currentUser, ($user) =>
  $user?.isImpersonating && $user.companyId ? String($user.companyId) : null
);

export const accessToken = derived(auth, $auth => $auth.accessToken);
export const isImpersonating = derived(currentUser, $user => $user?.isImpersonating === true);
